import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Download, RefreshCw, LogOut, Search, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "muyassr_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  // Try saved token on mount
  useEffect(() => {
    const t = localStorage.getItem(STORAGE_KEY);
    if (t) tryLogin(t, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryLogin = async (t, silent = false) => {
    try {
      await axios.post(`${API}/admin/login`, { token: t });
      localStorage.setItem(STORAGE_KEY, t);
      setToken(t);
      setAuthed(true);
      if (!silent) toast.success("تم الدخول بنجاح");
      fetchLeads(t);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "رمز الدخول غير صحيح");
      localStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
    }
  };

  const fetchLeads = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t || token}` } });
      setLeads(res.data || []);
    } catch (e) {
      toast.error("تعذّر جلب التسجيلات");
      if (e?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setToken("");
    setLeads([]);
  };

  const exportCsv = () => {
    const url = `${API}/leads/export?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  const filtered = leads.filter((l) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return [l.full_name, l.phone, l.email, l.unit_model, l.message, l.source]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(t));
  });

  if (!authed) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center px-6" dir="rtl">
        <div className="w-full max-w-md bg-white border border-brand-border p-8 lg:p-10 shadow-sm" data-testid="admin-login">
          <div className="flex justify-center mb-6">
            <Logo size="lg" variant="cream" />
          </div>
          <div className="text-center mb-6">
            <div className="inline-flex w-12 h-12 rounded-full bg-brand-olive/10 text-brand-olive items-center justify-center mb-3">
              <Lock size={20} />
            </div>
            <h1 className="text-2xl font-serif text-brand-ink">لوحة تحكم مُيسر</h1>
            <p className="text-sm text-brand-mute mt-1">أدخل رمز الوصول للاطلاع على التسجيلات</p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              tryLogin(token);
            }}
            className="space-y-4"
          >
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="رمز الدخول"
              className="w-full bg-transparent border-b-2 border-brand-border focus:border-brand-olive py-3 text-base outline-none transition-colors text-center tracking-wider"
              data-testid="admin-token-input"
              required
            />
            <button
              type="submit"
              className="w-full bg-brand-olive hover:bg-brand-olive-dark text-brand-cream py-3 text-sm font-medium rounded-sm transition-colors"
              data-testid="admin-login-btn"
            >
              دخول
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream" dir="rtl" data-testid="admin-dashboard">
      <header className="bg-brand-ink text-brand-cream py-5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between gap-4">
          <Logo size="md" variant="dark" />
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchLeads()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-cream/10 hover:bg-brand-cream/15 text-sm rounded-sm"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              تحديث
            </button>
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-accent hover:bg-brand-accent-dark text-brand-ink text-sm rounded-sm"
              data-testid="admin-export-btn"
            >
              <Download size={14} />
              تصدير CSV
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-200 text-sm rounded-sm"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} />
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-3xl font-serif text-brand-ink">التسجيلات</h2>
            <p className="text-sm text-brand-mute mt-1">إجمالي: <span className="text-brand-olive font-medium">{leads.length}</span> تسجيل</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute end-3 top-1/2 -translate-y-1/2 text-brand-mute" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="بحث بالاسم أو الجوال..."
              className="w-full bg-white border border-brand-border focus:border-brand-olive py-2.5 px-4 pe-10 text-sm rounded-sm outline-none"
              data-testid="admin-search"
            />
          </div>
        </div>

        <div className="bg-white border border-brand-border overflow-x-auto" data-testid="admin-table-wrapper">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead className="bg-brand-olive text-brand-cream text-xs">
              <tr>
                <th className="text-right px-4 py-3">#</th>
                <th className="text-right px-4 py-3">الاسم</th>
                <th className="text-right px-4 py-3">الجوال</th>
                <th className="text-right px-4 py-3">البريد</th>
                <th className="text-right px-4 py-3">النموذج</th>
                <th className="text-right px-4 py-3">الرسالة</th>
                <th className="text-right px-4 py-3">المصدر</th>
                <th className="text-right px-4 py-3">Sheets</th>
                <th className="text-right px-4 py-3">التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-16 text-brand-mute">
                    {loading ? "جارٍ التحميل..." : "لا توجد تسجيلات مطابقة"}
                  </td>
                </tr>
              )}
              {filtered.map((l, i) => (
                <tr
                  key={l.id || i}
                  className="border-t border-brand-border/60 hover:bg-brand-cream/40"
                  data-testid={`admin-row-${i}`}
                >
                  <td className="px-4 py-3 text-brand-mute">{i + 1}</td>
                  <td className="px-4 py-3 text-brand-ink font-medium">{l.full_name}</td>
                  <td className="px-4 py-3 text-brand-ink" dir="ltr">{l.phone}</td>
                  <td className="px-4 py-3 text-brand-mute">{l.email || "—"}</td>
                  <td className="px-4 py-3">
                    {l.unit_model ? (
                      <span className="inline-block px-2 py-0.5 bg-brand-olive/10 text-brand-olive text-xs rounded">
                        {l.unit_model}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-brand-mute max-w-xs truncate" title={l.message || ""}>{l.message || "—"}</td>
                  <td className="px-4 py-3 text-brand-mute text-xs">{l.source || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${l.sheets_synced ? "bg-green-500" : "bg-brand-border"}`} />
                  </td>
                  <td className="px-4 py-3 text-brand-mute text-xs whitespace-nowrap">
                    {l.created_at ? new Date(l.created_at).toLocaleString("ar-SA") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
